/**
 * The decisions behind the Swoft slot picker of the booked-meeting form,
 * kept out of the component so they can be tested without a DOM.
 *
 * When the client runs the Swoft CRM, the caller does not type a time: they
 * pick one of the free slots of the person who holds that client's meetings,
 * read from Swoft over the next 14 days. Everything here works in the
 * browser's local time, which is the time the caller reads and the time the
 * booked-meeting request is built from.
 */

export interface AvailabilitySlot {
  /** ISO 8601 instant. */
  start: string;
  end: string;
}

/** How far ahead the picker looks, today included. */
export const SLOT_PICKER_DAYS = 14;

/** Local calendar day as "YYYY-MM-DD", the key slots are grouped by. */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/** The days the picker navigates, starting today, as local midnights. */
export function pickerDays(today: Date, count: number = SLOT_PICKER_DAYS): Date[] {
  const first = startOfDay(today);
  return Array.from({ length: count }, (_, i) => new Date(first.getFullYear(), first.getMonth(), first.getDate() + i));
}

/**
 * The window asked of Swoft: from now (nothing already past is bookable) to
 * the local midnight closing the last day of the picker.
 */
export function availabilityWindow(now: Date, count: number = SLOT_PICKER_DAYS): { from: Date; to: Date } {
  const first = startOfDay(now);
  return { from: now, to: new Date(first.getFullYear(), first.getMonth(), first.getDate() + count) };
}

/**
 * Slots per local day, each day sorted by start time. Slots already started
 * at `now` are dropped: a button for a time that has passed books nothing
 * useful. Unreadable dates are dropped as well.
 */
export function groupSlotsByDay(slots: AvailabilitySlot[], now?: Date): Map<string, AvailabilitySlot[]> {
  const byDay = new Map<string, AvailabilitySlot[]>();
  const nowMs = now ? now.getTime() : Number.NEGATIVE_INFINITY;
  const sorted = slots
    .map((slot) => ({ slot, at: new Date(slot.start).getTime() }))
    .filter((entry) => !Number.isNaN(entry.at) && entry.at >= nowMs)
    .sort((a, b) => a.at - b.at);
  for (const { slot } of sorted) {
    const key = dayKey(new Date(slot.start));
    const list = byDay.get(key);
    if (list) list.push(slot);
    else byDay.set(key, [slot]);
  }
  return byDay;
}

/** "HH:MM" in local time, 24-hour, as the rest of the form shows times. */
export function formatSlotTime(iso: string): string {
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Day heading of the picker, e.g. "lundi 29 septembre" in the given locale. */
export function formatPickerDay(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
}

/**
 * What the form records when a slot is picked: the same fields the manual
 * date and time entry fills, so buildBookedMeetingRequest does not change.
 */
export function slotToChoice(slot: AvailabilitySlot): { year: number; month: number; day: number; hour: string; minute: string } {
  const date = new Date(slot.start);
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
    hour: String(date.getHours()).padStart(2, '0'),
    minute: String(date.getMinutes()).padStart(2, '0'),
  };
}

/** True when the host's Outlook calendar was read as well as Swoft's. */
export function includesOutlook(sources: readonly string[] | null | undefined): boolean {
  return Array.isArray(sources) && sources.some((s) => s.toLowerCase() === 'outlook');
}

/** The first day of the picker that has a free slot, or the first day. */
export function firstDayWithSlots(days: Date[], byDay: Map<string, AvailabilitySlot[]>): number {
  const index = days.findIndex((day) => (byDay.get(dayKey(day))?.length ?? 0) > 0);
  return index === -1 ? 0 : index;
}
